import { useSearch } from "@tanstack/react-router";
import queryClient from "api/queryClient.ts";
import { useExportCommissionsMutation, useGetCommissionsQuery } from "api/TenantSlug/Commission/hooks.ts";
import {
    useDestroyCommissionGroupMutation,
    useUpdateCommissionGroupMutation,
} from "api/TenantSlug/CommissionGroup/hooks.ts";
import { AddCommissionDrawer } from "components/Drawers/AddCommissionDrawer/AddCommissionDrawer.tsx";
import { DetailCommissionDrawer } from "components/Drawers/DetailCommissionDrawer/DetailCommissionDrawer.tsx";
import { EditCommissionDrawer } from "components/Drawers/EditCommissionDrawer/EditCommissionDrawer.tsx";
import { EditCommissionGroupDrawer } from "components/Drawers/EditCommissionGroupDrawer/EditCommissionGroupDrawer.tsx";
import { userGroup } from "components/Tables/CommissionGroupTable/temp.ts";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { usePermission } from "hooks/usePermissions.ts";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { COMMISSIONS_PRIORITY_SORT } from "localConstants.ts";
import { FC, Key } from "react";
import { useRecoilValue } from "recoil";
import { CommissionType, PermissionEnum } from "types/enums.ts";
import { DrawerId, TableId } from "types/models.ts";

import CommissionData = App.Data.CommissionData;
import GetCommissionResponse = App.Http.Responses.Commission.GetCommissionResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetCommissionsResponse = App.Http.Responses.Commission.GetCommissionsResponse;
import CascadeData = App.Data.CascadeData;

interface Props {
    offer?: App.Data.OfferData;
    cascade?: CascadeData;
    columns: TableColumnType<CommissionData>[];
    isLoading?: boolean;
    editPermission?: PermissionEnum;
    commissionGroupId?: string | null;
    onRefetchTable?: (refetchFn: () => void) => void;
}

interface SearchParams {
    [`editCommission-id`]: string;
    [`detailCommission-id`]: string;
    [`editCommissionGroup-id`]: string;
}

export const CommissionsTable: FC<Props> = ({
    offer,
    cascade,
    columns,
    isLoading,
    editPermission,
    commissionGroupId,
    onRefetchTable,
}) => {
    const permissionsUtils = usePermission();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const exportState = useRecoilValue(exportAtom(TableId.CommissionsTable));
    const { editingRowId, filter, page } = useRecoilValue(tableAtom<CommissionData>()(TableId.CommissionsTable));
    const fetchCommissionListQuery = useGetCommissionsQuery("commissions", {
        page: { number: page?.number, size: page?.size },
        filter: {
            filter: {
                ...(offer ? { "offer.id": offer.id, is_enabled: true } : {}),
                ...(cascade ? { "offer.id": cascade.offer_id, cascade_id: cascade.id, is_enabled: true } : {}),
                ...(filter?.filter ?? {}),
                commission_type: commissionGroupId || offer || cascade ? CommissionType.User : CommissionType.Tenant,
                ...(commissionGroupId && {
                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                }),
            },
            sort: filter?.sort ?? COMMISSIONS_PRIORITY_SORT,
            include:
                "offer,user,cascade,parent,parent.user,parent.offer,offer.country,children,children.commissionGroup,commissionGroup",
        },
    });
    const destroyFormStructureMutation = useDestroyCommissionGroupMutation();
    const updateFormStructureMutation = useUpdateCommissionGroupMutation(editingRowId || "");

    const exportCommissionsMutation = useExportCommissionsMutation();

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<CommissionData>(
        {
            entityName: "commission",
            editPermission: editPermission,
            detailPermission: PermissionEnum.CommissionView,
            detailDrawerId: DrawerId.EditCommission,
            detailDrawerParam: `${DrawerId.EditCommission}-id`,
        },
        {
            additionalActions: [
                {
                    type: "drawer",
                    drawerId: DrawerId.DetailCommission,
                    drawerParam: `${DrawerId.DetailCommission}-id`,
                    permission: PermissionEnum.CommissionView,
                    condition: () => (editPermission ? !permissionsUtils.hasPermission(editPermission) : true),
                },
            ],
        }
    );

    const { tableData, tableState, exportData, refetchData, editTableData, deleteTableData } =
        useTableDataApiOperations<GetCommissionsResponse, GetCommissionResponse, CommissionData, BaseExportRequest>(
            TableId.CommissionsTable,
            fetchCommissionListQuery,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            destroyFormStructureMutation,
            updateFormStructureMutation,
            exportCommissionsMutation
        );

    onRefetchTable?.(refetchData);

    return (
        <>
            {!commissionGroupId && permissionsUtils.hasPermission(PermissionEnum.CommissionCreate) && (
                <AddCommissionDrawer onSubmit={() => refetchData()} offerId={offer?.id} />
            )}
            {permissionsUtils.hasPermission(PermissionEnum.CommissionGroupUpdate) && (
                <>
                    {searchParams[`${DrawerId.EditCommissionGroup}-id`] && (
                        <EditCommissionGroupDrawer
                            commissionGroupId={searchParams[`${DrawerId.EditCommissionGroup}-id`]}
                        />
                    )}
                </>
            )}
            {searchParams[`${DrawerId.EditCommission}-id`] && (
                <EditCommissionDrawer
                    commissionId={searchParams[`${DrawerId.EditCommission}-id`]}
                    onSubmit={() => {
                        queryClient.invalidateQueries({
                            queryKey: ["getCommissions"],
                        });
                        setTimeout(() => {
                            refetchData();
                        }, 100); //dirty hot fix - why not working without delay?? TODO refactor whole table refetch logic bcs its shit due to antd..
                    }}
                />
            )}
            {searchParams[`${DrawerId.DetailCommission}-id`] && (
                <DetailCommissionDrawer commissionId={searchParams[`${DrawerId.DetailCommission}-id`]} />
            )}
            <Table
                tableId={TableId.CommissionsTable}
                data={tableData || []}
                dataSource={tableData?.commissions ?? []}
                columnConfig={columns}
                loading={!tableData?.commissions || isLoading || fetchCommissionListQuery.isPending}
                onRefresh={refetchData}
                showRefetchButton={true}
                enableFilters={true}
                searchDataIndex="offer.title"
                defaultDelete={deleteTableData}
                allowExpand={true}
                defaultOnUpdate={editTableData}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) =>
                    editPermission && permissionsUtils.hasPermission(editPermission)
                        ? getRowHandlers(record, rowIndex)
                        : {}
                }
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                expandable={{ childrenColumnName: "antdChildren" }}
                addEntityData={
                    !commissionGroupId
                        ? {
                              drawerId: DrawerId.AddCommission,
                              permission: PermissionEnum.CommissionCreate,
                          }
                        : undefined
                }
                enableWordWrap={true}
                onExport={() =>
                    exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page,
                        filter: {
                            filter: {
                                ...(tableState.filter?.filter ?? {}),
                                commission_type: commissionGroupId ? CommissionType.User : CommissionType.Tenant,
                                ...(commissionGroupId && {
                                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                                }),
                            },
                        },
                        delimiter: exportState.delimiter,
                    })
                }
                autoScrollWidth={true}
            />
        </>
    );
};
